import type { ClientDetail, UsageDay, UsageTotals } from './api';
import { count, money } from './format';

const compact = (n: number) => n.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const minutes = (s: number) => (s < 60 ? `${s} sec` : `${Math.round(s / 60).toLocaleString('en-US')} min`);
const cost = (u: Partial<UsageTotals>) => (u.smsCostMicros ?? 0) + (u.aiCostMicros ?? 0);
const texts = (u: Partial<UsageTotals>) => (u.smsOut ?? 0) + (u.smsIn ?? 0);

const ROWS: { label: string; value: (u: UsageTotals) => string; warn?: (u: UsageTotals) => boolean }[] = [
  { label: 'Texts sent', value: (u) => count(u.smsOut) },
  { label: 'Texts received', value: (u) => count(u.smsIn) },
  { label: 'Texts that failed', value: (u) => count(u.smsOutFailed), warn: (u) => u.smsOutFailed > 0 },
  { label: 'Picture texts', value: (u) => count(u.mmsOut + u.mmsIn) },
  { label: 'Calls in', value: (u) => count(u.calls) },
  { label: 'Missed calls', value: (u) => count(u.missedCalls) },
  { label: 'Calls put through to the owner', value: (u) => (u.forwardedCalls ? `${count(u.forwardedCalls)}, ${minutes(u.forwardSeconds)}` : '0') },
  { label: 'AI replies', value: (u) => count(u.aiCalls) },
  { label: 'AI tokens', value: (u) => (u.aiCalls ? `${compact(u.aiInTokens)} in, ${compact(u.aiOutTokens)} out` : '0') },
  { label: 'Address checks', value: (u) => count(u.addressChecks) },
  { label: 'Emails sent', value: (u) => count(u.emails) },
];

/** The last 31 days, oldest first, with empty days filled in (days are UTC, as counted). */
function lastDays(days: UsageDay[]): UsageDay[] {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const out: UsageDay[] = [];
  for (let i = 30; i >= 0; i--) {
    const key = new Date(Date.now() - i * 86_400_000).toISOString().slice(0, 10);
    out.push(byDay.get(key) ?? { day: key });
  }
  return out;
}

const label = (key: string) => new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** What this shop used, and what it cost LeadsIRL: this month and the last 30 days, then day by day. */
export function Usage({ d }: { d: ClientDetail }) {
  const { month, last30 } = d.usage;
  const series = lastDays(d.usage.days);
  const busy = [...series].reverse().filter((x) => texts(x) || x.calls || x.aiCalls || cost(x));
  const most = Math.max(0, ...series.map(texts));
  const top = Math.max(1, most);
  const W = 12, GAP = 4, H = 96;

  return (
    <div className="usage">
      <section className="card">
        <h2>Totals</h2>
        <table className="plain totals">
          <thead><tr><th /><th className="num">This month</th><th className="num">Last 30 days</th></tr></thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className={`num${r.warn?.(month) ? ' warn' : ''}`}>{r.value(month)}</td>
                <td className={`num${r.warn?.(last30) ? ' warn' : ''}`}>{r.value(last30)}</td>
              </tr>
            ))}
            <tr className="group"><td colSpan={3}>What it cost LeadsIRL</td></tr>
            <tr><td>Texting (Telnyx)</td><td className="num">{money(month.smsCostMicros)}</td><td className="num">{money(last30.smsCostMicros)}</td></tr>
            <tr><td>AI (Bedrock)</td><td className="num">{money(month.aiCostMicros)}</td><td className="num">{money(last30.aiCostMicros)}</td></tr>
            <tr className="total"><td>Total</td><td className="num">{money(cost(month))}</td><td className="num">{money(cost(last30))}</td></tr>
          </tbody>
        </table>
        <p className="fine">Texting is what Telnyx charged for each text. AI is worked out from the tokens in and out at Bedrock's prices. Phone minutes and the number itself are not counted here yet.</p>
      </section>

      <section className="card">
        <h2>Texts per day</h2>
        <div className="chart">
          <svg viewBox={`0 0 ${series.length * (W + GAP)} ${H + 18}`} role="img" aria-label="Texts per day, last 31 days" preserveAspectRatio="none">
            {series.map((x, i) => {
              const all = texts(x);
              const failed = Math.min(all, x.smsOutFailed ?? 0);
              const h = all ? Math.max(2, (all / top) * H) : 0;
              const hf = failed ? Math.max(2, (failed / top) * H) : 0;
              const left = i * (W + GAP);
              return (
                <g key={x.day}>
                  <title>{`${label(x.day)}: ${count(all)} texts${failed ? `, ${failed} failed` : ''}, ${money(cost(x))}`}</title>
                  <rect x={left} y={0} width={W} height={H} fill="transparent" />
                  {all === 0 && <rect x={left} y={H - 1} width={W} height={1} fill="#D5DAE0" />}
                  {h > 0 && <rect x={left} y={H - h} width={W} height={h} rx={1.5} fill="#1B2229" />}
                  {hf > 0 && <rect x={left} y={H - hf} width={W} height={hf} rx={1.5} fill="#B42318" />}
                </g>
              );
            })}
          </svg>
          <div className="chart-axis"><span>{label(series[0]!.day)}</span><span>Today</span></div>
        </div>
        <p className="fine">Busiest day: {count(most)} texts. Red is texts that failed. Days are counted in UTC, so each one ends at 8 PM Eastern (7 PM in winter).</p>
      </section>

      <section className="card">
        <h2>Day by day</h2>
        {busy.length === 0 ? <p className="empty">Nothing used in the last 31 days.</p> : (
          <table className="plain days">
            <thead>
              <tr>
                <th>Day</th>
                <th className="num">Texts sent</th>
                <th className="num">Received</th>
                <th className="num">Failed</th>
                <th className="num c-calls">Calls</th>
                <th className="num c-ai">AI replies</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {busy.map((x) => (
                <tr key={x.day}>
                  <td>{label(x.day)}</td>
                  <td className="num">{count(x.smsOut ?? 0)}</td>
                  <td className="num">{count(x.smsIn ?? 0)}</td>
                  <td className={`num${x.smsOutFailed ? ' warn' : ''}`}>{count(x.smsOutFailed ?? 0)}</td>
                  <td className="num c-calls">{count(x.calls ?? 0)}</td>
                  <td className="num c-ai">{count(x.aiCalls ?? 0)}</td>
                  <td className="num">{money(cost(x))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
